import { Component, useMemo, useRef, useState, useCallback } from "react";
import { api, API, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Move, Plus, Trash2, Save, X, Loader2, RotateCw, Grid3x3 } from "lucide-react";
import { pdfBgImage, EditorGridBg } from "./RoofPdfView";
import { usePanelSelection, SelectionToolbar, MarqueeRect } from "./usePanelSelection";
import { useEditorViewport, ViewportToolbar, svgPoint } from "./useEditorViewport";
import { useSatOffset, SatAlignControls, SatImage } from "./SatAlign";
import { AreaChips, AreaSettingsPanel } from "./AreaSettings";
import { usePanelString, StringButton, StringDialog, StringPreview, rotateGroup, GroupRotate } from "./PanelString";

// ---- geometria (metri locali) ----
function rectCorners(cx, cy, w, h, angDeg) {
  const a = (angDeg * Math.PI) / 180, hw = w / 2, hh = h / 2;
  return [[-hw, -hh], [hw, -hh], [hw, hh], [-hw, hh]].map(([dx, dy]) => ({
    x: cx + dx * Math.cos(a) - dy * Math.sin(a),
    y: cy + dx * Math.sin(a) + dy * Math.cos(a),
  }));
}
function pointInPoly(pt, poly) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i].x, yi = poly[i].y, xj = poly[j].x, yj = poly[j].y;
    if ((yi > pt.y) !== (yj > pt.y) && pt.x < ((xj - xi) * (pt.y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}
function edgeAngleDeg(c0, c1) { return (Math.atan2(c1.y - c0.y, c1.x - c0.x) * 180) / Math.PI; }
function rot(p, deg, o) {
  if (!o || !isFinite(o.x) || !isFinite(o.y)) return { x: p.x, y: p.y };
  const a = (deg * Math.PI) / 180, dx = p.x - o.x, dy = p.y - o.y;
  return { x: o.x + dx * Math.cos(a) - dy * Math.sin(a), y: o.y + dx * Math.sin(a) + dy * Math.cos(a) };
}

export default function PanelLayoutEditor({ roof, params, chosen, leadId, moduleInfo, selfToken, onSaved, onClose, startFullscreen = false }) {
  return (
    <EditorErrorBoundary onClose={onClose}>
      <PanelLayoutEditorInner roof={roof} params={params} chosen={chosen} leadId={leadId} moduleInfo={moduleInfo} selfToken={selfToken} onSaved={onSaved} onClose={onClose} startFullscreen={startFullscreen} />
    </EditorErrorBoundary>
  );
}

class EditorErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err, info) { console.error("PanelLayoutEditor crash:", err, info); }
  render() {
    if (this.state.err) {
      return (
        <div data-testid="panel-layout-editor-error" className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm">
          <p className="font-semibold text-destructive mb-1">Si è verificato un problema nell'editor pannelli.</p>
          <p className="text-muted-foreground mb-3">L'operazione non è stata salvata. Riapri l'editor per riprovare (i pannelli salvati in precedenza sono al sicuro).</p>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => this.setState({ err: null })} data-testid="editor-error-retry">Riprova</Button>
            <Button size="sm" variant="ghost" onClick={this.props.onClose} data-testid="editor-error-close">Chiudi</Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function PanelLayoutEditorInner({ roof, params, chosen, leadId, moduleInfo, selfToken, onSaved, onClose, startFullscreen }) {
  const geo = useMemo(() => {
    const center = roof.center || {};
    const lat0 = center.lat, lng0 = center.lng;
    const mlat = 111320.0, mlng = 111320.0 * Math.cos((lat0 * Math.PI) / 180);
    const toM = (c) => ({ x: (c.lng - lng0) * mlng, y: (c.lat - lat0) * mlat });
    const toLL = (x, y) => ({ lat: +(lat0 + y / mlat).toFixed(8), lng: +(lng0 + x / mlng).toFixed(8) });
    const mod = moduleInfo || roof.module || {};
    const prodW = mod.larghezza_mm ? mod.larghezza_mm / 1000 : null;
    const prodH = mod.lunghezza_mm ? mod.lunghezza_mm / 1000 : null;
    const pw = Number(prodW ?? params?.panel_w_m ?? 1.0), ph = Number(prodH ?? params?.panel_h_m ?? 1.7), gap = Number(params?.gap_m ?? 0.025);
    const w = chosen === "orizzontale" ? ph : pw;
    const h = chosen === "orizzontale" ? pw : ph;
    const wp = Number(mod.potenza_wp) || 450;
    const falde = roof.falde || [];
    const faldaMeters = {}, faldaCentroid = {}, faldaAng = {};
    const excl = [];
    const all = [];
    falde.forEach((f) => {
      const mp = (f.coordinates || []).map(toM);
      faldaMeters[f.id] = mp;
      const cen = mp.reduce((s, p) => ({ x: s.x + p.x, y: s.y + p.y }), { x: 0, y: 0 });
      faldaCentroid[f.id] = { x: cen.x / (mp.length || 1), y: cen.y / (mp.length || 1) };
      // angolo di riferimento = lato più lungo della falda
      let best = -1, ang = 0;
      for (let i = 0; i < mp.length; i++) {
        const j = (i + 1) % mp.length, d = Math.hypot(mp[j].x - mp[i].x, mp[j].y - mp[i].y);
        if (d > best) { best = d; ang = edgeAngleDeg(mp[i], mp[j]); }
      }
      faldaAng[f.id] = ang;
      mp.forEach((p) => all.push(p));
      (f.esclusioni || []).forEach((e) => {
        const ep = (e.coordinates || []).map(toM);
        if (ep.length >= 3) { excl.push(ep); ep.forEach((p) => all.push(p)); }
      });
    });
    const xs = all.map((p) => p.x), ys = all.map((p) => p.y);
    let minx = Math.min(...xs), maxx = Math.max(...xs), miny = Math.min(...ys), maxy = Math.max(...ys);
    const mx = (maxx - minx) * 0.18 + 1, my = (maxy - miny) * 0.18 + 1;
    minx -= mx; maxx += mx; miny -= my; maxy += my;
    const AR = 1.5;
    let cw = maxx - minx, ch = maxy - miny;
    if (cw / ch > AR) { const nh = cw / AR, d = (nh - ch) / 2; miny -= d; maxy += d; }
    else { const nw = ch * AR, d = (nw - cw) / 2; minx -= d; maxx += d; }
    const W = 960, H = Math.round(W / AR);
    const px = (p) => ({ x: ((p.x - minx) / (maxx - minx)) * W, y: ((maxy - p.y) / (maxy - miny)) * H });
    const mFromPx = (sx, sy) => ({ x: minx + (sx / W) * (maxx - minx), y: maxy - (sy / H) * (maxy - miny) });
    const sw = toLL(minx, miny), ne = toLL(maxx, maxy);
    const satUrl = `${API}/roof/satellite?bbox=${sw.lng},${sw.lat},${ne.lng},${ne.lat}&w=${W}&h=${H}`;
    const pdfImg = pdfBgImage(roof, toM, px);
    return { pdfImg, toM, toLL, w, h, gap, wp, faldaMeters, faldaCentroid, faldaAng, excl, falde, W, H, px, mFromPx, satUrl, spanX: maxx - minx };
  }, [roof, params, chosen, moduleInfo]);

  const idRef = useRef(1);
  const fromSaved = (pn) => {
      const mp = (pn.corners || []).map(geo.toM);
      const cx = mp.reduce((s, p) => s + p.x, 0) / (mp.length || 1);
      const cy = mp.reduce((s, p) => s + p.y, 0) / (mp.length || 1);
      // Ricostruisci ESATTAMENTE il rettangolo dal poligono salvato (dimensioni + angolo
      // per-pannello), così l'editor combacia con il layout automatico invece di forzare
      // una taglia globale che stravolge la disposizione.
      let ang, w, h;
      if (mp.length >= 3) {
        ang = edgeAngleDeg(mp[0], mp[1]);
        w = Math.hypot(mp[1].x - mp[0].x, mp[1].y - mp[0].y);
        h = Math.hypot(mp[2].x - mp[1].x, mp[2].y - mp[1].y);
      } else {
        ang = geo.faldaAng[pn.falda_id] ?? 0; w = geo.w; h = geo.h;
      }
      if (!isFinite(w) || w <= 0) w = geo.w;
      if (!isFinite(h) || h <= 0) h = geo.h;
      return { id: idRef.current++, cx, cy, falda_id: pn.falda_id, ang, w, h };
  };
  const [panels, setPanels] = useState(() => (roof.panel_layout?.panels || []).map(fromSaved));
  const [area, setArea] = useState(null);
  const apiBase = selfToken ? `/public/self/${selfToken}` : `/leads/${leadId}`;
  const replaceArea = (fid, saved) => {
    const fresh = saved.map(fromSaved);
    setPanels((ps) => [...ps.filter((p) => p.falda_id !== fid), ...fresh]);
    S.setSel(new Set());
  };
  const [mode, setMode] = useState("move");
  const [snapOn, setSnapOn] = useState(false);
  const [saving, setSaving] = useState(false);
  const svgRef = useRef(null);

  const snap = useCallback((cx, cy, falda_id, excludeId) => {
    const Cf = geo.faldaCentroid[falda_id];
    if (!Cf || !isFinite(Cf.x) || !isFinite(Cf.y)) return { x: cx, y: cy };
    const ang = geo.faldaAng[falda_id] ?? 0;
    const anchorP = panels.find((p) => p.falda_id === falda_id && p.id !== excludeId);
    const anchor = anchorP ? { x: anchorP.cx, y: anchorP.cy } : Cf;
    const cr = rot({ x: cx, y: cy }, -ang, Cf), ar = rot(anchor, -ang, Cf);
    const pxp = geo.w + geo.gap, pyp = geo.h + geo.gap;
    const sr = { x: ar.x + Math.round((cr.x - ar.x) / pxp) * pxp, y: ar.y + Math.round((cr.y - ar.y) / pyp) * pyp };
    const res = rot(sr, ang, Cf);
    if (!isFinite(res.x) || !isFinite(res.y)) return { x: cx, y: cy };
    return res;
  }, [geo, panels]);

  const clientToM = (e) => {
    const p = svgPoint(svgRef.current, e);
    return p ? geo.mFromPx(p.x, p.y) : null;
  };
  const V = useEditorViewport(svgRef, geo.W, geo.H, startFullscreen);
  const SO = useSatOffset(roof);
  const showSat = !geo.pdfImg && !roof?.draw_mode;

  const S = usePanelSelection({ panels, setPanels, clientToM, geo, editable: true, mode });
  const sel = S.sel;
  const onPanelDown = S.onPanelDown;
  const onMove = S.onMove;
  const onUp = () => S.onUp(snapOn ? (p) => snap(p.cx, p.cy, p.falda_id, p.id) : null);
  const onBgDown = (e) => {
    if (S.onBgDown(e)) return;
    const m = clientToM(e);
    if (!m) return;
    let fid = null;
    if (area && pointInPoly(m, geo.faldaMeters[area] || [])) fid = area;
    for (const f of geo.falde) { if (fid) break; if (pointInPoly(m, geo.faldaMeters[f.id])) { fid = f.id; break; } }
    if (!fid && geo.falde.length) fid = geo.falde[0].id; // fuori falda: usa la prima come riferimento angolo
    const ang = fid ? (geo.faldaAng[fid] ?? 0) : 0;
    const pos = snapOn && fid ? snap(m.x, m.y, fid, null) : m;
    const nid = idRef.current++;
    setPanels((ps) => [...ps, { id: nid, cx: pos.x, cy: pos.y, falda_id: fid, ang, w: geo.w, h: geo.h }]);
    S.setSel(new Set([nid]));
  };
  const del = S.remove;
  const rotate = (deg) => { if (!sel.size) return; setPanels((ps) => rotateGroup(ps, sel, deg)); };
  const faldaAt = (m) => geo.falde.find((f) => pointInPoly(m, geo.faldaMeters[f.id] || []))?.id || (area || geo.falde[0]?.id || null);
  const ST = usePanelString({ pw: geo.w, ph: geo.h, gap: geo.gap, rowPitch: null, angAt: (m) => geo.faldaAng[faldaAt(m)] ?? 0,
    onPlace: (blk) => {
      const ids = [];
      const fid = blk.length ? faldaAt({ x: blk[0].cx, y: blk[0].cy }) : null;
      const add = blk.map((b) => { const nid = idRef.current++; ids.push(nid); return { id: nid, ...b, falda_id: faldaAt({ x: b.cx, y: b.cy }) || fid }; });
      setPanels((ps) => [...ps, ...add]);
      S.setSel(new Set(ids));
    } });
  const save = async () => {
    setSaving(true);
    try {
      const body = { panels: panels.map((p) => ({ falda_id: p.falda_id, corners: rectCorners(p.cx, p.cy, p.w ?? geo.w, p.h ?? geo.h, p.ang).map((c) => geo.toLL(c.x, c.y)) })) };
      const base = selfToken ? `/public/self/${selfToken}` : `/leads/${leadId}`;
      await SO.persist(base);
      const { data } = await api.put(`${base}/panel-layout/panels`, body);
      toast.success(`Layout salvato: ${data.summary?.count} pannelli (${data.summary?.kwp} kWp)`);
      onSaved(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const P = (m) => geo.px(m);
  return (
    <div data-testid="panel-layout-editor" className={V.wrapClass}>
      <StringDialog s={ST} wp={geo.wp} prefix="editor" />
      <div className={`flex flex-wrap items-center gap-2 ${V.fs ? "shrink-0" : "mb-2"}`}>
        <ViewportToolbar v={V} prefix="editor" />
        <Button size="sm" variant={mode === "move" ? "default" : "outline"} onClick={() => setMode("move")} data-testid="editor-mode-move"><Move className="h-4 w-4 mr-1" strokeWidth={1.5} />Sposta</Button>
        <Button size="sm" variant={mode === "add" ? "default" : "outline"} onClick={() => setMode("add")} data-testid="editor-mode-add"><Plus className="h-4 w-4 mr-1" strokeWidth={1.5} />Aggiungi</Button>
        <Button size="sm" variant="outline" onClick={del} disabled={!sel.size} data-testid="editor-delete"><Trash2 className="h-4 w-4 mr-1" strokeWidth={1.5} />Elimina</Button>
        <StringButton s={ST} prefix="editor" />
        <RotateCw className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
        <GroupRotate disabled={!sel.size} onRotate={rotate} prefix="editor" />
        <Button size="sm" variant={snapOn ? "default" : "outline"} onClick={() => setSnapOn((s) => !s)} data-testid="editor-snap"><Grid3x3 className="h-4 w-4 mr-1" strokeWidth={1.5} />Griglia: {snapOn ? "ON" : "OFF"}</Button>
        <SelectionToolbar s={S} prefix="editor" />
        {showSat && <SatAlignControls s={SO} prefix="editor" />}
        <span className="text-sm font-semibold ml-1" data-testid="editor-count">{panels.length} pannelli · {((panels.length * geo.wp) / 1000).toFixed(2)} kWp</span>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="ghost" onClick={onClose} data-testid="editor-cancel"><X className="h-4 w-4 mr-1" />Annulla</Button>
          <Button size="sm" onClick={save} disabled={saving} data-testid="editor-save">{saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" strokeWidth={1.5} />}Salva</Button>
        </div>
      </div>
      <AreaChips falde={geo.falde} panels={panels} wp={geo.wp} sel={area} onSel={setArea} />
      {area && geo.falde.find((f) => f.id === area) && (
        <AreaSettingsPanel key={area} falda={geo.falde.find((f) => f.id === area)} index={geo.falde.findIndex((f) => f.id === area)}
          base={apiBase} onPanels={replaceArea} onClose={() => setArea(null)} />
      )}
      <p className={`text-[11px] text-muted-foreground mb-2 ${V.fs ? "hidden" : ""}`}>
        Zoom con rotellina o pizzico, sposta la vista con tasto centrale, Alt+trascina o due dita. Modifica libera: le falde (giallo) e gli ingombri (rosso) restano come riferimento, ma puoi mettere i pannelli dove vuoi. {mode === "move" ? "Trascina un pannello per spostarlo: il movimento è fine e continuo, così puoi affiancarli o sfalsarli a piacere." : "Clicca per aggiungere un pannello."} Selezione multipla: trascina su un'area vuota per selezionare con un rettangolo, oppure Ctrl/Shift+clic (su mobile "Selezione multipla"); poi trascina il blocco, usa le frecce (5 cm), Canc per eliminare, Esc per deselezionare. I pannelli selezionati si possono ruotare (90° o +15°) o eliminare. Con "Griglia" (di default OFF) attivi l'aggancio automatico a griglia.
      </p>
      <div className={`w-full rounded-lg border border-border overflow-hidden ${V.fs ? "flex-1 min-h-0" : ""}`} style={{ touchAction: "none" }}>
        <svg ref={svgRef} viewBox={V.viewBox} className={V.svgClass} data-testid="editor-svg"
          onPointerDownCapture={(e) => { if (ST.active && e.button === 0 && !e.altKey) { e.stopPropagation(); ST.place(); return; } V.onDown(e); }}
          onPointerMove={(e) => { if (ST.active) ST.move(clientToM(e)); if (!V.onMove(e)) onMove(e); }} onPointerUp={(e) => { if (!V.onUp(e)) onUp(); }} onPointerLeave={(e) => { if (!V.onUp(e)) onUp(); }}>
          {geo.pdfImg ? <><rect x="0" y="0" width={geo.W} height={geo.H} fill="#fff" /><image href={geo.pdfImg.href} x={geo.pdfImg.x} y={geo.pdfImg.y} width={geo.pdfImg.width} height={geo.pdfImg.height} preserveAspectRatio="none" data-testid="editor-pdf-bg" /></> : roof?.draw_mode ? <EditorGridBg geo={geo} /> : <><rect x="0" y="0" width={geo.W} height={geo.H} fill="#1f2a1f" /><SatImage geo={geo} off={SO.off} /></>}
          <rect x="0" y="0" width={geo.W} height={geo.H} fill="transparent" onPointerDown={onBgDown} style={{ cursor: mode === "add" ? "copy" : "crosshair" }} data-testid="editor-bg" />
          {geo.falde.map((f) => {
            const pts = (geo.faldaMeters[f.id] || []).map((m) => { const p = P(m); return `${p.x.toFixed(1)},${p.y.toFixed(1)}`; }).join(" ");
            const on = area === f.id;
            return <polygon key={f.id} points={pts} fill={on ? "#FDD00326" : "none"} stroke={on ? "#FF8A00" : "#FDD003"} strokeWidth={on ? 4 : 2.5} pointerEvents="none" data-testid={on ? "editor-area-selected" : undefined} />;
          })}
          {geo.excl.map((ex, i) => {
            const pts = ex.map((m) => { const p = P(m); return `${p.x.toFixed(1)},${p.y.toFixed(1)}`; }).join(" ");
            return <polygon key={`e${i}`} points={pts} fill="#DC262688" stroke="#DC2626" strokeWidth="2" pointerEvents="none" />;
          })}
          {panels.map((p) => {
            const corners = rectCorners(p.cx, p.cy, p.w ?? geo.w, p.h ?? geo.h, p.ang);
            if (corners.some((c) => !isFinite(c.x) || !isFinite(c.y))) return null;
            const pts = corners.map((m) => { const q = P(m); return `${q.x.toFixed(1)},${q.y.toFixed(1)}`; }).join(" ");
            const on = sel.has(p.id);
            return <polygon key={p.id} points={pts} fill={on ? "#FDD003CC" : "#1E3A8ACC"} stroke={on ? "#B45309" : "#0B1B4D"} strokeWidth={on ? 2.5 : 1} data-testid={`editor-panel-${p.id}`} data-selected={on ? "1" : "0"}
              style={{ cursor: mode === "move" ? "grab" : "pointer" }} onPointerDown={(e) => onPanelDown(e, p)} />;
          })}
          <MarqueeRect box={S.box} />
          <StringPreview s={ST} P={P} />
        </svg>
      </div>
    </div>
  );
}
